// Shared config for the marketing Sentiment dashboard (prompt 50).

export const SOURCE_META = {
  google_review: { icon: '⭐', label: 'Google' },
  facebook_review: { icon: '📘', label: 'Facebook' },
  yelp_review: { icon: '🍴', label: 'Yelp' },
  tripadvisor_review: { icon: '🦉', label: 'TripAdvisor' },
  applemaps_review: { icon: '🍎', label: 'Apple Maps' },
  bbb_review: { icon: '🏛️', label: 'BBB' },
  other_review: { icon: '⭐', label: 'Review' },
  twitter: { icon: '🐦', label: 'Twitter/X' },
  facebook_post: { icon: '📘', label: 'Facebook' },
  instagram: { icon: '📷', label: 'Instagram' },
  linkedin: { icon: '💼', label: 'LinkedIn' },
  reddit: { icon: '🤖', label: 'Reddit' },
  news: { icon: '📰', label: 'News' },
  blog: { icon: '📝', label: 'Blog' },
  social_other: { icon: '💬', label: 'Social' },
};

export function sourceMeta(sourceType) {
  return SOURCE_META[sourceType] || { icon: '💬', label: sourceType || 'Mention' };
}

// Human-managed mention statuses (marketing triage).
export const MENTION_STATUSES = [
  // "unreviewed" is stored/sent to the API unchanged; it displays as
  // "Business as Usual" on the sentiment dashboard (prompt 55).
  { value: 'unreviewed', label: 'Business as Usual' },
  { value: 'glowing', label: 'Glowing' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'client_solution_center', label: 'Client Solution Center' },
  { value: 'resolved', label: 'Resolved' },
];

export const MENTION_STATUS_LABELS = Object.fromEntries(
  MENTION_STATUSES.map((s) => [s.value, s.label]),
);

export const MENTION_SEVERITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// 5-point sentiment scale (prompt 53), best → worst, with badge colors.
export const SENTIMENT_META = {
  very_positive: { label: 'Very Positive', color: '#276749' },
  positive: { label: 'Positive', color: '#38A169' },
  neutral: { label: 'Neutral', color: '#718096' },
  negative: { label: 'Negative', color: '#DD6B20' },
  very_negative: { label: 'Very Negative', color: '#E53E3E' },
};

export const SENTIMENT_OPTIONS = [
  { value: '', label: 'All Sentiment' },
  { value: 'very_positive', label: 'Very Positive' },
  { value: 'positive', label: 'Positive' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'negative', label: 'Negative' },
  { value: 'very_negative', label: 'Very Negative' },
];

// Color for an average sentiment score on the 1-5 scale: green ≥4, gold 3-3.9,
// red < 3. Used by the dashboard "Avg Sentiment Score" card.
export function sentimentScoreColor(score) {
  if (score == null) return 'var(--color-text-muted)';
  if (score >= 4.0) return '#38A169';
  if (score >= 3.0) return '#D69E2E';
  return '#E53E3E';
}

// Source filter options for the dashboard filter bar.
export const SOURCE_FILTERS = [
  { value: '', label: 'All Sources' },
  { value: 'google_review', label: 'Google Reviews' },
  { value: 'facebook_post', label: 'Facebook' },
  { value: 'twitter', label: 'Twitter/X' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'yelp_review', label: 'Yelp' },
  { value: 'news', label: 'News' },
];

// Product filter options (mirrors the backend SENTIMENT_PRODUCTS taxonomy).
export const PRODUCT_OPTIONS = [
  { value: '', label: 'All Products' },
  { value: 'any', label: 'Any Product' },
  { value: 'checking', label: 'Checking' },
  { value: 'savings', label: 'Savings' },
  { value: 'cd', label: 'CD' },
  { value: 'money-market', label: 'Money Market' },
  { value: 'hsa', label: 'HSA' },
  { value: 'ira-retirement', label: 'IRA / Retirement' },
  { value: 'mortgage', label: 'Mortgage' },
  { value: 'home-equity', label: 'Home Equity' },
  { value: 'auto-loan', label: 'Auto Loan' },
  { value: 'personal-loan', label: 'Personal Loan' },
  { value: 'credit-card', label: 'Credit Card' },
  { value: 'debit-card', label: 'Debit Card' },
  { value: 'online-banking', label: 'Online Banking' },
  { value: 'mobile-app', label: 'Mobile App' },
  { value: 'new-account', label: 'New Account' },
  { value: 'account-fees', label: 'Account Fees' },
  { value: 'business-accounts', label: 'Business Accounts' },
  { value: 'business-loan', label: 'Business Loan' },
  { value: 'sba', label: 'SBA' },
  { value: 'merchant-services', label: 'Merchant Services' },
  { value: 'wealth-management', label: 'Wealth Management' },
  { value: 'other', label: 'Other' },
];

export const PRODUCT_LABELS = Object.fromEntries(
  PRODUCT_OPTIONS.filter((o) => o.value && o.value !== 'any').map((o) => [o.value, o.label]),
);

export function formatMentionDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function ratingStars(rating) {
  if (!rating) return '—';
  const n = Math.max(0, Math.min(5, Math.round(rating)));
  return '★'.repeat(n) + '☆'.repeat(5 - n);
}
